/**
 * useGameAccess — polls the pilot's entitlement snapshot and returns a
 * simple boolean gate for gameplay routes.
 *
 * Access modes:
 *   - "paid"          — bought the $2.99 unlock, full access forever
 *   - "grandfathered" — registered before the paywall went live, free
 *   - "trial"         — inside the 7-day free trial window
 *   - "expired"       — trial ended, must buy to keep playing
 *   - "needs_trial"   — brand-new pilot, hasn't clicked "start trial" yet
 *
 * `allowed` collapses paid/grandfathered/trial into one boolean.
 */
import { useCallback, useEffect, useState } from "react";
import api from "./api";

export default function useGameAccess({ autoStartTrial = false } = {}) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/entitlements/me");
      setSnapshot(data);
      // On first-load for a fresh pilot, auto-start the 7-day trial.
      // We do this ONLY when the caller explicitly opts in (Landing
      // page CTA button, first game click, etc.) so trials aren't
      // burned just by browsing.
      if (autoStartTrial && data?.access === "needs_trial") {
        const started = await api.post("/entitlements/start-trial");
        setSnapshot(started.data);
      }
    } catch (e) {
      // Unauthenticated — leave snapshot null so callers can decide
      // whether to route to /login.
      setSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, [autoStartTrial]);

  useEffect(() => { refresh(); }, [refresh]);

  const allowed = snapshot
    ? ["paid", "grandfathered", "trial"].includes(snapshot.access)
    : false;

  return { snapshot, loading, allowed, refresh };
}
